import { __assign, __rest } from "tslib";
import { useEffect, useRef } from 'react';
import { getRefElement } from '../../utils';
import { useEventCallback } from '../useEventCallback';
/**
 * bind event when component mount and auto remove event when destroy.
 * can remove listener by call returned function or pass empty 'target'.
 *
 * @param target
 * listener will be removed when target is changed to null,
 * or rebinding to new element when `ref.current` change
 * @returns a method object
 * - listen() listen listener again, if that have listener, that will not listen again
 * - remove() remove listener
 * @example
 * ```ts
 * useEventListener(window, 'keyup', () => console.log('window key up'))
 * ```
 */
export function useEventListener(target, key, callback) {
    var args = [];
    for (var _i = 3; _i < arguments.length; _i++) {
        args[_i - 3] = arguments[_i];
    }
    var _a = getListenerOverloadOption(args), options = _a.options, config = _a.config;
    var _b = (config || {}).startImmediately, startImmediately = _b === void 0 ? true : _b;
    var listener = useEventCallback(callback);
    var cancelRef = useRef(function () { });
    var bindRef = useRef(function () { });
    var currentRefElm = getRefElement(target);
    useEffect(function () {
        if (!target)
            return;
        var currentElm = getRefElement(target);
        if (!currentElm) {
            return cancelRef.current;
        }
        bindRef.current = function () { return currentElm.addEventListener(key, listener, options); };
        cancelRef.current = function () {
            return currentElm.removeEventListener(key, listener, options);
        };
        if (startImmediately) {
            bindRef.current();
        }
        return cancelRef.current;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, currentRefElm]);
    return {
        listen: function () { return bindRef.current(); },
        remove: function () { return cancelRef.current(); },
    };
}
function getListenerOverloadOption(args) {
    var options;
    var config;
    if (typeof args[0] === 'boolean') {
        options = args[0];
        config = args[1];
    }
    else {
        var _a = __assign(__assign({}, (args[0] || {})), (args[1] || {})), startImmediately = _a.startImmediately, restOptions = __rest(_a, ["startImmediately"]);
        options = restOptions;
        config = {
            startImmediately: startImmediately,
        };
    }
    return { options: options, config: config };
}
